import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import Modal from '../Modal/Modal';
import ModalBody from '../Modal/ModalBody';
import ModalButtons from '../Modal/ModalButtons';
import MultiEmail from '../InputFields/MultiEmailField';
import { referralEmailValidationSchema } from '../../validation/referralEmailValidationSchema';
import FormContainer from '../FormContainer/FormContainer';
import { useReferAFriendEvent } from '../../util/helpers/referralCodeHelper';
import { getCurrentAccount } from '../../store/selectors/accountSelectors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import config from '../../config';
import Anchor from '../Anchor/Anchor';
import {
  Containers,
  ReferralTexts,
  ReferralButton,
  DecorativeCTA,
} from './sub-components';
import DecorativeEmojis from './sub-components/DecorativeEmojis';

const {
  ReferralBannerContainer,
  TextContainer,
  CloseButton,
  FirstRowContainer,
  SubtitleContainer,
  DesktopSubtitleContainer,
} = Containers;
const { ReferralTitle, ReferralSubtitle, ReferralBannerText } = ReferralTexts;
const { BackgroundPattern, Flex, Error } = DecorativeCTA;

const HAA_PMC_URL =
  'https://hardassetsalliance.com/referral-program-terms-and-conditions';
const GSS_PMC_URL =
  'https://goldsilver.com/referral-program-terms-and-conditions';

const ReferralBanner = () => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isReferredSuccessfully, setIsReferredSuccessfully] = useState(false);

  // Local state for banner visibility
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [isHiding, setIsHiding] = useState(false);

  const account = useSelector(getCurrentAccount);
  const settings = useSelector(selectSettings);
  const { brokerTheme } = config;
  const isHAA = brokerTheme === 'HardAssetsAlliance';
  const broker = isHAA ? 'Hard Assets Alliance' : 'GoldSilver';

  const handleReferAFriend = useReferAFriendEvent();

  const submitForm = (values) => {
    handleReferAFriend(account, values.emails, handleOnSuccess, handleErrored);
  };

  const handleErrored = (error) => {
    const emailsWithErrors = error.map((err) => {
      const currentEmail = err?.Message.split('_')[0] || '';
      const trimmedEmail = currentEmail.trim();
      return `"${trimmedEmail}"`;
    });

    const uniqueEmails = [...new Set(emailsWithErrors)];
    const count = uniqueEmails.length;

    const errorMessageKey =
      count === 1 ? 'referral.errorMessage_one' : 'referral.errorMessage_other';
    const formattedErrorMessage = t(errorMessageKey, {
      count, // i18next uses this to choose _one or _other
      emails: uniqueEmails.join(', '),
      brokerName: broker || t('common.defaultBrokerName'),
    }).replace(/&quot;/g, '"');

    setErrorMessage(formattedErrorMessage);
    setIsReferredSuccessfully(false);
  };

  const handleOnSuccess = (data) => {
    setIsReferredSuccessfully(data);
  };

  const handleModalClose = () => {
    setIsReferredSuccessfully(false);
    setIsModalOpen(false);
  };

  // Handle banner close with smooth animation
  const handleCloseBanner = () => {
    setIsHiding(true);
    // Hide the banner after animation completes
    setTimeout(() => {
      setIsBannerVisible(false);
    }, 300);
  };

  // Don't render if referral program is not active or banner is hidden
  if (!settings?.IsReferralProgramActive || !isBannerVisible) {
    return null;
  }

  return (
    <div>
      <ReferralBannerContainer isHiding={isHiding}>
        <BackgroundPattern />
        <DecorativeEmojis />

        {/* Close button */}
        <CloseButton onClick={handleCloseBanner} aria-label="Close banner">
          ×
        </CloseButton>

        {/* First row - Title and Button on mobile, normal layout on desktop */}
        <FirstRowContainer>
          <TextContainer>
            <ReferralTitle>{t('referral.title')}</ReferralTitle>
            {/* Desktop subtitle - hidden on mobile */}
            <DesktopSubtitleContainer>
              <ReferralSubtitle>
                {t('referral.subtitle', {
                  amount: settings && settings?.ReferralAmount,
                  brokerName: broker || t('common.defaultBrokerName'),
                })}
              </ReferralSubtitle>
            </DesktopSubtitleContainer>
          </TextContainer>

          <ReferralButton onClick={() => setIsModalOpen(true)}>
            {t('referral.clickHere')}
          </ReferralButton>
        </FirstRowContainer>

        {/* Second row - Subtitle on mobile only */}
        <SubtitleContainer>
          <ReferralSubtitle>
            {t('referral.subtitle', {
              amount: settings && settings?.ReferralAmount,
              brokerName: broker || t('common.defaultBrokerName'),
            })}
          </ReferralSubtitle>
        </SubtitleContainer>
      </ReferralBannerContainer>

      <Modal
        title={t('referral.title')}
        size="md"
        isOpen={isModalOpen}
        close={handleModalClose}
      >
        <ModalBody>
          {isReferredSuccessfully ? (
            <>
              <ReferralBannerText>
                {t('referral.referralSuccessMessage')}
              </ReferralBannerText>
              <ModalButtons
                isHorizontal
                marginTop
                primaryButtonProps={{
                  label: t('common.ok'),
                  onClick: handleModalClose,
                }}
                secondaryButtonProps={{
                  onClick: handleModalClose,
                }}
              />
            </>
          ) : (
            <Formik
              initialValues={{
                emails: [],
                currentEmail: '',
              }}
              validationSchema={referralEmailValidationSchema}
              onSubmit={submitForm}
              validateOnBlur={false}
            >
              {({ values }) => (
                <Form>
                  <ReferralBannerText>
                    {t('referral.message', {
                      amount: settings && settings?.ReferralAmount,
                      brokerName: broker || t('common.defaultBrokerName'),
                    })}
                    <Anchor
                      bold
                      type="website"
                      marginBottom={32}
                      value={isHAA ? HAA_PMC_URL : GSS_PMC_URL}
                      text={t(`referral.pmc`)}
                    />
                  </ReferralBannerText>
                  <FormContainer>
                    <Field name="emails" label={t('referral.emailLabel')}>
                      {({ field }) => (
                        <MultiEmail
                          {...field}
                          setErrorMessage={setErrorMessage}
                        />
                      )}
                    </Field>
                    <Flex>
                      {errorMessage && <Error>{errorMessage}</Error>}
                      <ModalButtons
                        isHorizontal
                        marginTop
                        primaryButtonProps={{
                          label: t('common.confirm'),
                          type: 'submit',
                          disabled: values.emails.length === 0,
                        }}
                        secondaryButtonProps={{
                          onClick: handleModalClose,
                        }}
                      />
                    </Flex>
                  </FormContainer>
                </Form>
              )}
            </Formik>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default React.memo(ReferralBanner);
